import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import Button from "../../ui/Button";
import DropdownMenu from "../../ui/DropdownMenu";
import AddAdminModal from "./AddAdmin";
import EditAdminModal from "./EditAdmin";
import DisableAdminModal from "./AdminDeleteSuccess";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import WhiteTitledCard from "./../../../components/cards/WhiteTitledCard";
import {
  fetchUsersAsync,
  resetAdminState,
} from "../../../redux/features/adminSlice";
import moment from "moment";
import ExportUsersCsvButton from "../user-management/components/ExportUsersCsvButton";
import { useUserFilters } from "../user-management/useUserFilters";
import UserFilterBar from "../user-management/UserFilterBar";

const AdminTable: React.FC = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<any>();
  const [disabledAgent, setDisabledAgent] = useState(false);
  const { users, isLoading } = useAppSelector((state) => state.admin);
  const filters = useUserFilters("ADMIN");
  const dispatch = useAppDispatch();

  const columns = [
    { label: "S/N", field: "id" },
    { label: "Name of Admin", field: "admin_name" },
    { label: "Username", field: "username" },
    { label: "Phone Number", field: "phone_number" },
    { label: "Email", field: "email" },
    { label: "Country", field: "country" },
    { label: "Reg. Date", field: "date_added" },
    { label: "Action", field: "action" },
  ];

  const dropdownOptions = [
    {
      label: "Edit admin",
      onClick: (admin: any) => {
        setOpenEdit(true);
        setSelectedAdmin(admin);
      },
      returnAll: true,
    },
    {
      label: <span className="text-red-500">Disable admin</span>,
      onClick: (admin: any) => {
        setDisabledAgent(true);
        setSelectedAdmin(admin);
      },
      returnAll: true,
    },
  ];

  const adminData = users?.data?.data?.map((admin: any, index: number) => {
    return {
      id: index + 1,
      admin_name: admin?.fullName ?? "N/A",
      username: admin?.username ?? "N/A",
      phone_number: admin?.phoneNumber ?? "N/A",
      country: admin?.country?.name ?? "N/A",
      email: admin?.email ?? "N/A",
      action: (
        <DropdownMenu
          options={dropdownOptions}
          icon={<MoreVertical style={{ cursor: "pointer" }} />}
          item={admin}
        />
      ),
      date_added: moment(admin?.createdAt).format("MMM DD, YYYY"),
    };
  });

  useEffect(() => {
    dispatch(resetAdminState());
    dispatch(fetchUsersAsync(filters.listParams));
  }, [dispatch, filters.listParams]);

  const tabList = [
    { id: 1, name: "All Admins", count: users?.data?.totalItems ?? 0 },
  ];

  const refetchAdmins = () => {
    dispatch(fetchUsersAsync(filters.listParams));
  };

  return (
    <WhiteTitledCard>
      <div className="px-6 sm:p-6">
        <UserFilterBar
          filters={filters}
          userType="ADMIN"
          panelId="admin-filter-panel"
          searchPlaceholder="Search admin name, email, username"
          actions={
            <>
              <ExportUsersCsvButton
                totalItems={users?.data?.totalItems ?? 0}
                params={filters.exportParams}
              />
              <Button
                layout="toolbar"
                size="sm"
                label="Add admin"
                onClick={() => setOpenAdd(true)}
              />
            </>
          }
        />

        <div className="flex flex-col md:flex-row flex-wrap items-center gap-3 mb-4">
          {tabList?.map((tab) => (
            <div
              key={tab.id}
              className="space-x-2 cursor-pointer text-sm font-semibold p-2 text-black border-b-2 border-primary"
            >
              <span>{tab.name}</span>
              {tab?.count ? (
                <span className="bg-lightPurple px-2 text-accent rounded-md">
                  {tab?.count}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        <CustomTable
          data={{ columns, rows: adminData }}
          loading={isLoading}
          showFooter={true}
          noRecordText="No Admin Users Available"
          totalPages={users?.data?.totalPages}
          itemsPerPage={users?.data?.itemsPerPage}
          currentPage={filters.page}
          onPageChange={(value: number) => filters.setPage(value)}
          onLimitChange={(value: number) => {
            filters.setLimit(value);
            filters.setPage(0);
          }}
        />
        <AddAdminModal
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onSave={refetchAdmins}
        />
        <EditAdminModal
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          selectedAdmin={selectedAdmin}
        />
        <DisableAdminModal
          isOpen={disabledAgent}
          onClose={() => setDisabledAgent(false)}
          adminId={selectedAdmin?.id}
          onConfirm={refetchAdmins}
        />
      </div>
    </WhiteTitledCard>
  );
};

export default AdminTable;
